/**
 * space_sky — the sector's sky (report §9 R2 + the R1 space IBL + the R7 star streaks).
 *
 * BACKGROUND. The ground night sky's own pieces, re-aimed at a vacuum: the starless 4K Milky Way photo and
 * the 114 k-star catalogue drawn as analytic round points (`buildStarField` — per-star colour, the no-popping
 * cell grid), with NO scintillation (`airW = 0`), no extinction, no airglow; the star gain chosen from the
 * catalogue so its top ~1 % of peak radiance crosses 1.2 and blooms (`starGainFor`), and the photo at a low
 * gain (SPACE_BG_GAIN) for deep blacks. The lookup direction is ROTATED per system (`systemSkyQuat` — sys0
 * keeps its ground sky, every other system lands under a different orientation of the same photo).
 * `scene.backgroundNode` is handed over through SkyModule.setSpaceBackground (activate / deactivate only).
 *
 * IBL. `scene.environment` in space is a 64×32 equirect: a near-black starlight floor plus a warm lobe in the
 * sun's direction (metal hulls glint toward the sun instead of mirroring a blue Commons sky); three PMREMs
 * it. Rewritten only when the sun direction moves more than ~3° (alt systems) — `needsPMREMUpdate`.
 *
 * STREAKS (R7). The catalogue's brightest stars again as an instanced clip-space quad layer that exists
 * only at speed: each star stretched radially AWAY from the apex (the screen point the ship flies at) by
 * `starStreak(speed)` — overcruise starts it, hyper saturates it. The layer follows the camera and carries
 * the inverse sky rotation so its stars sit exactly on the background's.
 *
 * Session resources (textures, the node graph, the streak geometry) are built ONCE and tagged
 * `userData.shared` — the sector sweep never frees them (the GPU shield law: nothing drawn is destroyed).
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float, normalize, positionWorldDirection, texture as tslTexture, equirectUV,
  attribute, cameraProjectionMatrix, modelViewMatrix, positionGeometry, screenSize, uv, length, clamp,
} from 'three/tsl';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { StarFieldInput } from '../sky/star_field.js';
import { buildSpaceStarField } from './space_stars.js';
import { parseStarCatalog, bucketStars, equirectDirection, STAR_GRID, STAR_SHRINK, type StarCatalog } from '../sky/star_catalog.js';
import { quantile, SPACE_BLOOM, SPACE_BG_GAIN, SPACE_STAR_GAMMA, SPACE_STAR_P99_TARGET, spaceStarRadiance, systemSkyQuat, type V3 } from './space_look.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** A representative pixel floor (chord/pixel ≈ 1080p at the chase FOV) for the gain percentile. */
const GAIN_PX_FLOOR = 0.001;
/** Streak-layer population: the brightest stars of the catalogue. */
const STREAK_STARS = 1400;
const STREAK_RADIUS = 1_000_000;

export interface SpaceSkyAssets { bg: THREE.Texture | null; stars: StarFieldInput | null; catalog: StarCatalog | null }

let assetsP: Promise<SpaceSkyAssets> | null = null;
let assets: SpaceSkyAssets | null = null;

/**
 * Load (once per session) the photo + the bucketed catalogue — reusing what the ground sky already loaded
 * (`shared`, SkyModule.nightSkyAssets) — and always the raw catalogue (the gain percentile + the streaks).
 */
export function loadSpaceSkyAssets(renderer: unknown, shared: { bg: THREE.Texture | null; stars: StarFieldInput | null } | null, maxPerCell: number): Promise<SpaceSkyAssets> {
  if (assetsP) return assetsP;
  const catP: Promise<StarCatalog | null> = fetch('/sky/night_sky_clear.stars.bin')
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((buf) => parseStarCatalog(buf))
    .catch(() => null);
  const bgP: Promise<THREE.Texture | null> = shared?.bg ? Promise.resolve(shared.bg) : new Promise((resolve) => {
    const ktx = new KTX2Loader().setTranscoderPath('/basis/');
    try { ktx.detectSupport(renderer as never); } catch { /* no renderer yet — the webp path below */ }
    ktx.load('/sky/night_sky_clear_bg.ktx2',
      (t) => { t.colorSpace = THREE.SRGBColorSpace; ktx.dispose(); resolve(t); },
      undefined,
      () => {
        ktx.dispose();
        new THREE.TextureLoader().load('/sky/night_sky_clear_bg.webp',
          (t) => { t.colorSpace = THREE.SRGBColorSpace; resolve(t); }, undefined, () => resolve(null));
      });
  });
  assetsP = Promise.all([bgP, catP]).then(([bg, catalog]) => {
    let stars: StarFieldInput | null = shared?.stars ?? null;
    if (!stars && catalog) {
      const b = bucketStars(catalog, STAR_GRID, maxPerCell);
      const mk = (data: Float32Array, w: number, h: number): THREE.DataTexture => {
        const t = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.FloatType);
        t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.flipY = false;
        t.needsUpdate = true;
        t.userData.shared = true;
        return t;
      };
      stars = { cells: mk(b.cells, b.cellsWidth, b.cellsHeight), records: mk(b.records, b.recordsWidth, b.recordsHeight), grid: b.grid, recordsWidth: b.recordsWidth, maxPerCell };
    }
    if (bg) bg.userData.shared = true;
    assets = { bg, stars, catalog };
    return assets;
  });
  return assetsP;
}

/** The catalogue's peak radiance per star at a pixel floor (the energy-conserving Gaussian's centre). */
export function catalogPeaks(cat: StarCatalog, pxFloor = GAIN_PX_FLOOR): Float32Array {
  const out = new Float32Array(cat.count);
  for (let i = 0; i < cat.count; i++) {
    const s = Math.max(cat.sigma[i] * STAR_SHRINK, pxFloor);
    out[i] = cat.flux[i] / (2 * Math.PI * s * s);
  }
  return out;
}

// ── the live uniforms (one set per session; the node graph reads them) ──
const uRot = uniform(new THREE.Matrix3());
const uStarGain = uniform(SPACE_STAR_P99_TARGET); // the radiance the 99th-percentile star lands at
const uInvP99 = uniform(1.33);
const uGamma = uniform(SPACE_STAR_GAMMA);
const uBgGain = uniform(SPACE_BG_GAIN);
const uStreak = uniform(0);
const uApex = uniform(new THREE.Vector2(0, 0));
const uApexOn = uniform(0);

export const spaceSkyState = { built: false, starGain: SPACE_STAR_P99_TARGET, p99: 0, bloomFrac: 0, medianStar: 0, bg: false, stars: 0, systemId: '', quat: [0, 0, 0, 1] as number[], streak: 0, envDir: [0, 1, 0] as number[] };

let bgNode: N = null;
/**
 * The space background node (built once): rotate the world view direction into the system's sky frame,
 * then photo × bg gain + the catalogue's stars × star gain. Null until the assets land.
 */
export function spaceBackgroundNode(): N {
  if (bgNode || !assets) return bgNode;
  const a = assets;
  if (a.catalog) {
    const peaks = catalogPeaks(a.catalog);
    const p99 = quantile(peaks, 0.99);
    (uInvP99 as unknown as { value: number }).value = p99 > 0 ? 1 / p99 : 1;
    let over = 0;
    for (let i = 0; i < peaks.length; i++) if (spaceStarRadiance(peaks[i], p99) > SPACE_BLOOM.threshold) over++;
    spaceSkyState.starGain = SPACE_STAR_P99_TARGET; spaceSkyState.p99 = p99; spaceSkyState.bloomFrac = over / Math.max(1, peaks.length);
    spaceSkyState.medianStar = spaceStarRadiance(quantile(peaks, 0.5), p99);
    spaceSkyState.stars = a.catalog.count;
  }
  const d = normalize((uRot as N).mul(positionWorldDirection));
  const bg = a.bg ? tslTexture(a.bg, equirectUV(d)).rgb.mul(uBgGain) : vec3(0);
  const stars = a.stars ? buildSpaceStarField(d, a.stars, { invP99: uInvP99, gamma: uGamma, target: uStarGain }) : vec3(0);
  bgNode = (bg as N).add(stars);
  spaceSkyState.built = true;
  spaceSkyState.bg = !!a.bg;
  return bgNode;
}

/** Point the sky at a system (sys0 = the Commons' own sky). Cheap — a uniform. Returns the quaternion. */
export function setSpaceSkySystem(systemId: string): THREE.Quaternion {
  const q = systemSkyQuat(systemId);
  const quat = new THREE.Quaternion(q[0], q[1], q[2], q[3]);
  // The background samples the SKY direction d_sky = R · d_world.
  (uRot as unknown as { value: THREE.Matrix3 }).value.setFromMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(quat));
  spaceSkyState.systemId = systemId;
  spaceSkyState.quat = [...q];
  return quat;
}

/** Live gains (probe levers). */
export function setSpaceSkyGains(star?: number, bg?: number, gamma?: number): void {
  if (gamma !== undefined) (uGamma as unknown as { value: number }).value = gamma;
  if (star !== undefined) (uStarGain as unknown as { value: number }).value = star;
  if (bg !== undefined) (uBgGain as unknown as { value: number }).value = bg;
}

// ── the R7 streak layer ─────────────────────────────────────────────────────────────────────────────────
let streakMesh: THREE.Mesh | null = null;
/**
 * The brightest catalogue stars as camera-following clip-space quads, stretched radially away from the apex.
 * Built once; the caller adds it under the sector root each activate (a follower) and sets its quaternion to
 * the INVERSE sky rotation so its stars sit on the background's.
 */
export function spaceStreakLayer(): THREE.Mesh | null {
  if (streakMesh) return streakMesh;
  const cat = assets?.catalog;
  if (!cat) return null;
  const idx = Array.from({ length: cat.count }, (_, i) => i).sort((i, j) => cat.flux[j] - cat.flux[i]).slice(0, STREAK_STARS);
  const centers = new Float32Array(idx.length * 3), colors = new Float32Array(idx.length * 4);
  const fMax = cat.flux[idx[0]] || 1;
  idx.forEach((s, k) => {
    centers[k * 3] = cat.dir[s * 3] * STREAK_RADIUS; centers[k * 3 + 1] = cat.dir[s * 3 + 1] * STREAK_RADIUS; centers[k * 3 + 2] = cat.dir[s * 3 + 2] * STREAK_RADIUS;
    const b = Math.max(0.25, Math.sqrt(cat.flux[s] / fMax));
    colors[k * 4] = cat.color[s * 3]; colors[k * 4 + 1] = cat.color[s * 3 + 1]; colors[k * 4 + 2] = cat.color[s * 3 + 2]; colors[k * 4 + 3] = b;
  });
  const quad = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.setIndex(quad.getIndex());
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  geo.setAttribute('aCenter', new THREE.InstancedBufferAttribute(centers, 3));
  geo.setAttribute('aColor', new THREE.InstancedBufferAttribute(colors, 4));
  geo.instanceCount = idx.length;
  geo.userData.shared = true;
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  markMrtOverlay(mat);
  mat.vertexNode = Fn(() => {
    const center = attribute('aCenter', 'vec3');
    const clip = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(center, 1.0)));
    const ndc = clip.xy.div(clip.w);
    const rel = ndc.sub(uApex);
    const radial = normalize(rel.add(vec2(1e-5, 0)));
    const across = vec2(radial.y.negate(), radial.x);
    // Length grows with the star's distance from the apex (a warp's radial field); the tail points AT the apex.
    const len = (uStreak as N).mul(length(rel)).mul(0.55).mul(uApexOn);
    const widthNdc = vec2(2.2).div(screenSize); // ~1.1 px half-width
    const g = positionGeometry.xy;
    const along = radial.mul(g.y.add(0.5).mul(len).negate());
    const side = across.mul(g.x.mul(2)).mul(widthNdc);
    return vec4(clip.xy.add(along.add(side).mul(clip.w)), clip.zw);
  })();
  const col = attribute('aColor', 'vec4');
  const head = float(1).sub(uv().y); // 1 at the star, 0 at the tail's end (toward the apex)
  mat.colorNode = vec3(col.xyz).mul(col.w).mul(4.0);
  mat.opacityNode = clamp(head.mul(head), 0, 1).mul(clamp((uStreak as N).mul(1.6), 0, 1));
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.name = 'space-star-streaks';
  mesh.renderOrder = -9;
  mat.userData.shared = true;
  streakMesh = mesh;
  return mesh;
}
/** Drive the streaks: strength 0..1 and the apex in NDC (null = the apex is behind the camera). */
export function setSpaceStreaks(strength: number, apexNdc: { x: number; y: number } | null): void {
  (uStreak as unknown as { value: number }).value = strength;
  (uApexOn as unknown as { value: number }).value = apexNdc ? 1 : 0;
  if (apexNdc) (uApex as unknown as { value: THREE.Vector2 }).value.set(apexNdc.x, apexNdc.y);
  spaceSkyState.streak = strength;
  if (streakMesh) streakMesh.visible = strength > 0.01 && !!apexNdc;
}

// ── the R1 space IBL ────────────────────────────────────────────────────────────────────────────────────
let envTex: THREE.DataTexture | null = null;
const ENV_W = 64, ENV_H = 32;
/**
 * The space environment: a near-black starlight floor + a warm lobe toward the sun (a tight glint lobe and a
 * faint broad one). `sunDir` world unit vector; `sunRgb` linear light colour. Reuses one texture (the PMREM
 * regenerates on `needsPMREMUpdate`).
 */
export function spaceEnvironment(sunDir: V3, sunRgb: [number, number, number]): THREE.DataTexture {
  const data = envTex ? (envTex.image.data as Float32Array) : new Float32Array(ENV_W * ENV_H * 4);
  const floor = [0.0035, 0.0045, 0.0075];
  for (let j = 0; j < ENV_H; j++) {
    for (let i = 0; i < ENV_W; i++) {
      const [x, y, z] = equirectDirection((i + 0.5) / ENV_W, (j + 0.5) / ENV_H);
      const c = x * sunDir.x + y * sunDir.y + z * sunDir.z;
      const lobe = 2.5 * Math.exp((c - 1) / 0.0025) + 0.04 * Math.exp((c - 1) / 0.35);
      const o = (j * ENV_W + i) * 4;
      data[o] = floor[0] + sunRgb[0] * lobe; data[o + 1] = floor[1] + sunRgb[1] * lobe; data[o + 2] = floor[2] + sunRgb[2] * lobe; data[o + 3] = 1;
    }
  }
  if (!envTex) {
    envTex = new THREE.DataTexture(data, ENV_W, ENV_H, THREE.RGBAFormat, THREE.FloatType);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    envTex.minFilter = THREE.LinearFilter; envTex.magFilter = THREE.LinearFilter; envTex.generateMipmaps = false;
    envTex.flipY = false;
    envTex.name = 'space-environment';
    envTex.userData.shared = true;
  }
  envTex.needsUpdate = true;
  envTex.needsPMREMUpdate = true;
  spaceSkyState.envDir = [sunDir.x, sunDir.y, sunDir.z];
  return envTex;
}
